import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import { AlertBannerContext } from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import {
  listSubscriptionsQuery,
  listSubscriptionsSuperAdminQuery,
  listAllSubscriptionsSuperAdminQuery,
  getSubscriptionQuery,
  createSubscriptionMutation,
  cancelSubscriptionMutation,
  pauseSubscriptionMutation,
  resumeSubscriptionMutation,
  extendSubscriptionMutation,
  activateSubscriptionMutation,
} from "../../Graphql/querries/subscriptionQuery";
import type { Subscription } from "../../Graphql/types/subscription";
import type { ListAllSubscriptionsSuperAdminInput } from "../../Graphql/types/subscription";
import {
  ListSubscriptionsResponse,
  ListAllSubscriptionsResponse,
  GetSubscriptionResponse,
  CreateSubscriptionResponse,
  CancelSubscriptionResponse,
  PauseSubscriptionResponse,
  ExtendSubscriptionResponse,
  ActivateSubscriptionResponse,
  ListSubscriptionsInput,
  GetSubscriptionInput,
  CreateSubscriptionInput,
  CancelSubscriptionInput,
} from "../../Graphql/types/subscription";
import { useDispatch } from "react-redux";
import {
  setSubscriptionList,
  setSubscriptionDetail,
} from "../../store/slices/subscriptionSlice";

export const useGetSubscriptionList = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<ListSubscriptionsResponse, Error, ListSubscriptionsInput>({
    mutationKey: ["listSubscriptions"],
    mutationFn: (input) =>
      fetchGraphQL(listSubscriptionsQuery, { input }, setAlert),
    onSuccess: (response) => {
      dispatch(
        setSubscriptionList(response.listSubscriptions.subscriptionList ?? []),
      );
    },
  });
};

/** Super admin: list subscriptions for a company (companyId in request). */
export const useGetSubscriptionListSuperAdmin = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    {
      listSubscriptionsSuperAdmin: {
        code: string;
        subscriptionList: Subscription[];
      };
    },
    Error,
    { companyId: string }
  >({
    mutationKey: ["listSubscriptionsSuperAdmin"],
    mutationFn: ({ companyId }) =>
      fetchGraphQL(listSubscriptionsSuperAdminQuery, { companyId }, setAlert),
  });
};

/** Super admin: list all subscriptions with search, filter, and pagination. */
export const useListAllSubscriptionsSuperAdmin = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    ListAllSubscriptionsResponse,
    Error,
    ListAllSubscriptionsSuperAdminInput | void
  >({
    mutationKey: ["listAllSubscriptionsSuperAdmin"],
    mutationFn: (input) =>
      fetchGraphQL(
        listAllSubscriptionsSuperAdminQuery,
        { input: input ?? {} },
        setAlert,
      ),
  });
};

export const useGetSubscriptionDetail = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<GetSubscriptionResponse, Error, GetSubscriptionInput>({
    mutationKey: ["getSubscription"],
    mutationFn: (input) =>
      fetchGraphQL(getSubscriptionQuery, { input }, setAlert),
    onSuccess: (response) => {
      dispatch(
        setSubscriptionDetail(response.getSubscription.subscription ?? null),
      );
    },
  });
};

export const useCreateSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<
    CreateSubscriptionResponse,
    Error,
    CreateSubscriptionInput
  >({
    mutationKey: ["createSubscription"],
    mutationFn: (input) =>
      fetchGraphQL(createSubscriptionMutation, { input }, setAlert),
  });
};

export const useCancelSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    CancelSubscriptionResponse,
    Error,
    CancelSubscriptionInput
  >({
    mutationKey: ["cancelSubscription"],
    mutationFn: (input) =>
      fetchGraphQL(cancelSubscriptionMutation, { input }, setAlert),
    onSuccess: (response) => {
      if (response.cancelSubscription.subscription) {
        dispatch(
          setSubscriptionDetail(response.cancelSubscription.subscription),
        );
      }
    },
  });
};

export const usePauseSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<PauseSubscriptionResponse, Error, string>({
    mutationKey: ["pauseSubscription"],
    mutationFn: (subscriptionId) =>
      fetchGraphQL(pauseSubscriptionMutation, { subscriptionId }, setAlert),
    onSuccess: (response) => {
      if (response.pauseSubscription.subscription) {
        dispatch(
          setSubscriptionDetail(response.pauseSubscription.subscription),
        );
      }
    },
  });
};

export const useResumeSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<ResumeSubscriptionResponse, Error, string>({
    mutationKey: ["resumeSubscription"],
    mutationFn: (subscriptionId) =>
      fetchGraphQL(resumeSubscriptionMutation, { subscriptionId }, setAlert),
    onSuccess: (response) => {
      if (response.resumeSubscription.subscription) {
        dispatch(
          setSubscriptionDetail(response.resumeSubscription.subscription),
        );
      }
    },
  });
};

export const useExtendSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    ExtendSubscriptionResponse,
    Error,
    { subscriptionId: string; subscriptionEndsAt: string }
  >({
    mutationKey: ["extendSubscription"],
    mutationFn: ({ subscriptionId, subscriptionEndsAt }) =>
      fetchGraphQL(
        extendSubscriptionMutation,
        {
          subscriptionId,
          subscriptionEndsAt,
        },
        setAlert,
      ),
    onSuccess: (response) => {
      if (response.extendSubscription.subscription) {
        dispatch(
          setSubscriptionDetail(response.extendSubscription.subscription),
        );
      }
    },
  });
};

export const useActivateSubscription = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const dispatch = useDispatch();

  return useMutation<
    ActivateSubscriptionResponse,
    Error,
    { subscriptionId: string; subscriptionEndsAt?: string }
  >({
    mutationKey: ["activateSubscription"],
    mutationFn: ({ subscriptionId, subscriptionEndsAt }) =>
      fetchGraphQL(
        activateSubscriptionMutation,
        {
          subscriptionId,
          subscriptionEndsAt: subscriptionEndsAt ?? null,
        },
        setAlert,
      ),
    onSuccess: (response) => {
      if (response.activateSubscription.subscription) {
        dispatch(
          setSubscriptionDetail(response.activateSubscription.subscription),
        );
      }
    },
  });
};
